import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaUserTie, FaBuilding, FaGlobe, FaDollarSign, FaCalendarAlt, FaShieldAlt, FaClipboardCheck } from "react-icons/fa";
import "./GettingStartedShared.css";

const General5 = () => {
    const navigate = useNavigate();

    const relatedArticles = [
          {
            title: "Can a Non-US Resident or Non-US Citizen Incorporate in the US?",
            description: "Learn about the requirements and process for non-residents to incorporate in the US.",
            link: "/can-a-non-us-resident-or-non-us-citizen-incorporate-in-the-us"
        },
        {
            title: "6 reasons why your startup should be incorporated in the US",
            description: "Discover the key benefits of incorporating your startup in the United States.",
            link: "/why-should-your-startup-incorporate-in-the-usa"
        },
        {
            title: "What Are the Ongoing Compliance Requirements After US Incorporation?",
            description: "Understand the legal and tax obligations for maintaining your US business entity.",
            link: "/what-are-the-ongoing-compliance-requirements-after-us-incorporation?"
        }
    ];

    const complianceRequirements = [
        {
            icon: <FaFileAlt className="gs-compliance-icon" />,
            title: "File Annual Reports",
            description: "Most states require corporations and LLCs to file an Annual Report (or Statement of Information) to keep your business information current.",
            details: [
                "Due: Usually yearly or biennially (varies by state)",
                "Fee: Varies by state",
                "Failure to file can result in penalties or administrative dissolution"
            ]
        },
        {
            icon: <FaUserTie className="gs-compliance-icon" />,
            title: "Maintain a Registered Agent",
            description: "You must have a registered agent with a physical address in your state of incorporation to receive legal documents.",
            details: [
                "Must be available during normal business hours",
                "Can be an individual or a professional service",
                "Must notify the state if your agent changes"
            ]
        },
        {
            icon: <FaBuilding className="gs-compliance-icon" />,
            title: "File Federal Tax Returns",
            description: "Corporations and LLCs must file federal tax returns with the IRS.",
            details: [
                "Corporations: File Form 1120 & Form 1120S",
                "LLCs (Multi-member): File Form 1065",
                "LLCs (Single-member): File Form 1040-NR + Schedule C (if treated as a disregarded entity)",
                "Individual: File Form 1040 or Form 1040-NR"
            ]
        },
        {
            icon: <FaDollarSign className="gs-compliance-icon" />,
            title: "Pay Franchise Taxes (If Applicable)",
            description: "Some states impose franchise taxes or similar fees, even if you didn't do any business.",
            details: [
                "Failure to pay = Penalties + loss of good standing"
            ]
        },
        {
            icon: <FaCalendarAlt className="gs-compliance-icon" />,
            title: "Keep Company Records Up to Date",
            description: "Maintain your operating agreement, meeting minutes, shareholder resolutions, and ownership structure for legal protection and investor confidence.",
            details: []
        },
        {
            icon: <FaShieldAlt className="gs-compliance-icon" />,
            title: "Renew Business Licenses (If Required)",
            description: "Depending on the type of business, you may need federal, state, or local licenses (e.g., sales tax permits, import/export licenses).",
            details: [
                "Check requirements in your operating state",
                "Renew licenses annually or as needed"
            ]
        },
        {
            icon: <FaGlobe className="gs-compliance-icon" />,
            title: "Comply with IRS Foreign Ownership Rules",
            description: "If you are a non-US resident, additional reporting (like Form 5472 for foreign-owned LLCs) may apply.",
            details: [
                "Deadline: Same as federal return usually (April 15)"
            ]
        }
    ];

    return (
        <div className="gs-container">
            <header className="gs-header">
                <div className="gs-container-1200">
                </div>
            </header>
            
            <main className="gs-main-content">
                <div className="gs-container-1200">
                    <nav className="gs-breadcrumb">
                        <ol>
                            <li><a href="/help">Help Center</a></li>
                            <li><a href="/getting-started">Getting Started</a></li>
                            <li>General</li>
                        </ol>
                    </nav>

                    <article className="gs-article">
                        <header className="gs-article-header">
                            <h1 className="gs-article-title">What Are the Ongoing Compliance Requirements After US Incorporation?</h1>
                            <div className="gs-article-meta">
                                <span>Posted by BooksComply Team</span>
                                <span>•</span>
                                <span>Last updated August 2025</span>
                            </div>
                        </header>
                        
                        <div className="gs-article-content">
                            <div className="gs-intro-card">
                                <FaClipboardCheck className="gs-intro-icon" />
                                <p>
                                    Incorporating your business in the United States is a major milestone—but it's only the beginning. To maintain your company's good standing and avoid penalties, you must comply with several ongoing federal and state-level requirements. Here's what you need to know:
                                </p>
                            </div>

                            <div className="gs-compliance-grid">
                                {complianceRequirements.map((requirement, index) => (
                                    <div key={index} className="gs-compliance-card">
                                        <div className="gs-compliance-icon-container">
                                            {requirement.icon}
                                        </div>
                                        <h2 className="gs-compliance-title">{requirement.title}</h2>
                                        <p className="gs-compliance-description">{requirement.description}</p>
                                        {requirement.details.length > 0 && (
                                            <ul className="gs-compliance-details">
                                                {requirement.details.map((detail, idx) => (
                                                    <li key={idx}>{detail}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="gs-cta-section">
                                <h2>Need Help Staying Compliant?</h2>
                                <p>
                                    Our firm specializes in helping international entrepreneurs stay fully compliant with all US business regulations. Whether it's annual filings, tax compliance, or registered agent services, we've got you covered.
                                </p>
                                <p>
                                    Contact us today to get personalized support and peace of mind.
                                </p>
                            </div>

                            {/* <div className="gs-helpful-section">
                                <h3 className="gs-helpful-title">Was this article helpful?</h3>
                                <div className="gs-helpful-buttons">
                                    <button className="gs-helpful-btn positive">
                                        <FaCheckCircle /> Yes, it was helpful
                                    </button>
                                    <button className="gs-helpful-btn negative">
                                        <FaTimesCircle /> No, I need more help
                                    </button>
                                </div>
                            </div> */}

                            <div className="gs-related-articles">
                                <h2 className="gs-related-title">Related Articles</h2>
                                <div className="gs-related-grid">
                                    {relatedArticles.map((article, index) => (
                                        <a 
                                            key={index} 
                                            href={article.link} 
                                            className="gs-related-card"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(article.link);
                                            }}
                                        >
                                            <h3>{article.title}</h3>
                                            <p>{article.description}</p>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </main>
        </div>
    );
};

export default General5;